import React, { useState } from 'react';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClinicalDemoFormPage() {
  const [form, setForm] = useState({
    full_name: '', phone: '', email: '', treatment_interest: '', source_channel: 'web_form', preferred_date: '', value_estimate: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const onChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const onSubmit = async (event) => {
    event.preventDefault();
    try {
      setSubmitting(true);
      setError('');
      const response = await fetch(`${API}/clinic/web-form`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          preferred_date: form.preferred_date || null,
          value_estimate: form.value_estimate ? Number(form.value_estimate) : null,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'No se pudo enviar la solicitud');
      setSubmitted(true);
      setForm({ full_name: '', phone: '', email: '', treatment_interest: '', source_channel: 'web_form', preferred_date: '', value_estimate: '', notes: '' });
    } catch (submitError) {
      setError(submitError.message || 'No se pudo enviar la solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO title="Demo Clinical Panel" description="Formulario demo para captar pacientes y llevarlos al CRM clínico de Neuralia369." />
      <main className="min-h-screen bg-background px-4 py-12 sm:px-6 lg:px-8" data-testid="clinical-demo-form-page">
        <div className="mx-auto max-w-6xl space-y-8">
          <section className="glass-card tracing-panel overflow-hidden" data-testid="clinical-demo-form-hero">
            <div className="grid gap-8 p-6 lg:grid-cols-[1.05fr_0.95fr] lg:p-10">
              <div>
                <p className="editorial-kicker" data-testid="clinical-demo-form-kicker">Captación clínica</p>
                <h1 className="mt-4 text-4xl font-semibold text-text-heading sm:text-5xl" data-testid="clinical-demo-form-heading">Formulario web conectado al CRM quirúrgico</h1>
                <p className="mt-5 max-w-2xl text-base text-text-body" data-testid="clinical-demo-form-subheading">Pensado para clínicas de cirugía plástica y medicina estética: capta interés, detecta tratamiento, estima valor y envía el lead directamente al Clinical Panel.</p>
                <div className="mt-6 grid gap-3 sm:grid-cols-3" data-testid="clinical-demo-form-benefits">
                  {[
                    'Entrada directa a CRM clínico',
                    'Prioridad comercial según interés y valor',
                    'Listo para enlazar con WhatsApp y TikTok',
                  ].map((item, index) => (
                    <div key={index} className="rounded-[22px] border border-border-subtle bg-background/50 p-4 text-sm text-text-body" data-testid={`clinical-demo-form-benefit-${index}`}>
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              <div className="glass-card p-6" data-testid="clinical-demo-form-card">
                {submitted ? (
                  <div className="space-y-4 text-center" data-testid="clinical-demo-form-success">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-primary">Lead registrado</p>
                    <h2 className="text-2xl font-semibold text-text-heading">Solicitud enviada correctamente</h2>
                    <p className="text-sm text-text-body">El lead ya ha entrado en el CRM clínico. Desde el panel interno se podrá mover por etapas, priorizar y hacer seguimiento.</p>
                    <button type="button" className="btn-primary w-full" onClick={() => setSubmitted(false)} data-testid="clinical-demo-form-success-reset-button">Enviar otro lead</button>
                  </div>
                ) : (
                  <form className="space-y-4" onSubmit={onSubmit} data-testid="clinical-demo-form-form">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Solicitud de valoración</p>
                      <h2 className="mt-2 text-2xl font-semibold text-text-heading">Déjanos tus datos</h2>
                      <p className="mt-2 text-sm text-text-body" data-testid="clinical-demo-form-card-helper">Este demo simula la entrada directa al CRM de la clínica con seguimiento, documentos y recordatorios preparados.</p>
                    </div>
                    <input className="input-kinesthetic" value={form.full_name} onChange={(event) => onChange('full_name', event.target.value)} placeholder="Nombre completo" data-testid="clinical-demo-form-name-input" />
                    <div className="grid gap-3 md:grid-cols-2">
                      <input className="input-kinesthetic" value={form.phone} onChange={(event) => onChange('phone', event.target.value)} placeholder="Teléfono / WhatsApp" data-testid="clinical-demo-form-phone-input" />
                      <input className="input-kinesthetic" value={form.email} onChange={(event) => onChange('email', event.target.value)} placeholder="Email" data-testid="clinical-demo-form-email-input" />
                    </div>
                    <input className="input-kinesthetic" value={form.treatment_interest} onChange={(event) => onChange('treatment_interest', event.target.value)} placeholder="Tratamiento de interés" data-testid="clinical-demo-form-treatment-input" />
                    <div className="grid gap-3 md:grid-cols-3">
                      <select className="input-kinesthetic" value={form.source_channel} onChange={(event) => onChange('source_channel', event.target.value)} data-testid="clinical-demo-form-source-select">
                        <option value="web_form">Web</option>
                        <option value="whatsapp">WhatsApp</option>
                        <option value="tiktok">TikTok</option>
                        <option value="instagram">Instagram</option>
                      </select>
                      <input type="datetime-local" className="input-kinesthetic" value={form.preferred_date} onChange={(event) => onChange('preferred_date', event.target.value)} data-testid="clinical-demo-form-date-input" />
                      <input type="number" className="input-kinesthetic" value={form.value_estimate} onChange={(event) => onChange('value_estimate', event.target.value)} placeholder="Presupuesto aprox." data-testid="clinical-demo-form-value-input" />
                    </div>
                    <textarea className="input-kinesthetic min-h-[120px] resize-none" value={form.notes} onChange={(event) => onChange('notes', event.target.value)} placeholder="Explícanos brevemente qué buscas, urgencia o dudas principales." data-testid="clinical-demo-form-notes-input" />
                    {error ? <p className="text-sm text-rose-300" data-testid="clinical-demo-form-error">{error}</p> : null}
                    <button type="submit" className="btn-primary w-full" disabled={submitting} data-testid="clinical-demo-form-submit-button">{submitting ? 'Enviando…' : 'Enviar al CRM clínico'}</button>
                  </form>
                )}
              </div>
            </div>
          </section>

          <section className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="glass-card p-6" data-testid="clinical-demo-form-crm-preview-card">
              <p className="editorial-kicker">Demo clínica</p>
              <h3 className="mt-3 text-2xl font-semibold text-text-heading">Lo que verá la clínica en su demo</h3>
              <div className="mt-6 grid gap-3 md:grid-cols-2">
                {[
                  'CRM de leads y pacientes con etapas y prioridades',
                  'Subida de documentos: consentimientos, presupuestos, analíticas e informes',
                  'Acciones preparadas para WhatsApp y email',
                  'Eventos, recordatorios y promociones desde un mismo panel',
                ].map((item, index) => (
                  <div key={index} className="rounded-[22px] border border-border-subtle bg-background/55 p-4 text-sm text-text-body" data-testid={`clinical-demo-form-crm-preview-${index}`}>{item}</div>
                ))}
              </div>
            </div>

            <div className="glass-card p-6" data-testid="clinical-demo-form-patient-preview-card">
              <p className="editorial-kicker">Demo paciente</p>
              <h3 className="mt-3 text-2xl font-semibold text-text-heading">Lo que verá el paciente en su portal</h3>
              <div className="mt-6 space-y-3">
                {[
                  'Journey del caso con etapa clínica y siguiente paso',
                  'Novedades, eventos y recordatorios visibles en portal',
                  'Simulación IA orientativa con imágenes del caso',
                  'Documentos y comunicaciones preparados de forma clara y privada',
                ].map((item, index) => (
                  <div key={index} className="rounded-[22px] border border-border-subtle bg-background/55 p-4 text-sm text-text-body" data-testid={`clinical-demo-form-patient-preview-${index}`}>{item}</div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}